import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, X, Users, Loader2, ShieldCheck } from 'lucide-react';
import { getPendingApprovals, getEmployees, approveLeaveRequest, rejectLeaveRequest } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { formatDate } from '@/lib/orgFormat';
import { useC, useDark } from '@/features/shared/theme/kaziColors';
import { useTranslation } from 'react-i18next';
import { useConfirmIfAvailable } from '@/features/shared/components/confirm/ConfirmProvider';
import { CoverageStatusBadge, confirmApproveAnyway } from '@/features/delegation/CoverageStatusBadge';
import { AssignCoverAction } from '@/features/delegation/AssignCoverAction';

interface PendingItem {
  id: string;
  name: string;
  /** The requester's auth user id (never offered as their own cover). */
  employee_user_id?: string;
  type: string;
  start_date: string;
  /** The signed "nothing needs covering" statement, when the employee gave
   *  one instead of naming a delegate. The approver has to see it: an
   *  attestation nobody reads is the silent opt-out it replaced. */
  coverage_attestation?: { statement?: string; signed_name?: string; signed_at?: string } | null;
  end_date?: string;
  days: number;
  reason?: string;
  /** Derived coverage (leave-service): shown prominently; never blocks. */
  coverage_status?: string | null;
  coverage_reason?: string | null;
  coverage_delegate_name?: string | null;
}

interface Props {
  onClose?: () => void;
  onChanged?: () => void;
}

const typeLabel = (t: string) => {
  const s = (t || '').toLowerCase();
  if (s.includes('sick')) return 'Sick';
  if (s.includes('wfh') || s.includes('home')) return 'Work From Home';
  return 'Vacation';
};

export const ApprovalsPanel: React.FC<Props> = ({ onChanged }) => {
  const C = useC();
  const dark = useDark();
  const [items, setItems] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const confirm = useConfirmIfAvailable();
  const { t } = useTranslation(['timeoff']);

  const load = async () => {
    setLoading(true);
    try {
      const [pending, emps] = await Promise.all([
        getPendingApprovals(),
        getEmployees({ page: 1, page_size: 100 }).catch(() => ({ items: [] as any[] })),
      ]);
      const byUser = new Map<string, any>((emps.items || []).map((e: any) => [e.user_id, e]));
      setItems((Array.isArray(pending) ? pending : []).map((r: any) => {
        const e = byUser.get(r.employee_id);
        return {
          id: r.id,
          employee_user_id: r.employee_id,
          name: e ? `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.email : 'Teammate',
          type: r.type, start_date: r.start_date, end_date: r.end_date, days: r.days, reason: r.reason,
          coverage_attestation: r.coverage_attestation ?? null,
          coverage_status: r.coverage_status ?? null,
          coverage_reason: r.coverage_reason ?? null,
          coverage_delegate_name: r.coverage_delegate_name ?? null,
        };
      }));
    } catch { setItems([]); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id: string) => {
    const it = items.find((x) => x.id === id);
    // Cover not confirmed → say so and let the approver decide (never a block;
    // approving anyway is recorded on the request).
    if (it && !(await confirmApproveAnyway(confirm, t as any, { status: it.coverage_status, reason: it.coverage_reason, delegateName: it.coverage_delegate_name }))) return;
    setBusy(id);
    try {
      await approveLeaveRequest(id);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success('Request approved');
      onChanged?.();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not approve the request'), { duration: 6000 });
    } finally { setBusy(null); }
  };

  const confirmReject = async (id: string) => {
    setBusy(id);
    try {
      await rejectLeaveRequest(id, reason.trim() || 'Not approved');
      setItems((xs) => xs.filter((x) => x.id !== id));
      setRejecting(null); setReason('');
      toast.success('Request rejected');
      onChanged?.();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not reject the request'), { duration: 6000 });
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div style={{ padding: '18px 24px', background: C.orange, color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Users size={18} />
        <span style={{ fontSize: 15, fontWeight: 700 }}>Pending approvals</span>
        {!loading ? <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.25)', borderRadius: 99, padding: '2px 10px' }}>{items.length}</span> : null}
      </div>

      {loading ? (
        <div style={{ padding: '48px 0', textAlign: 'center', color: C.charcoalLight }}><Loader2 size={24} className="animate-spin" /></div>
      ) : items.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: C.charcoalLight, fontSize: 14 }}>No requests waiting for approval 🎉</div>
      ) : (
        <div>
          {items.map((it, i) => (
            <div key={it.id} style={{ padding: '16px 24px', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: C.orange, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>{it.name.charAt(0)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: C.charcoal }}>{it.name}</p>
                  <p style={{ fontSize: 12, color: C.charcoalLight }}>{typeLabel(it.type)} · {it.start_date}{it.end_date && it.end_date !== it.start_date ? ` – ${it.end_date}` : ''} · {it.days} day(s)</p>
                  {it.reason ? <p style={{ fontSize: 11, color: C.charcoalSubtle, marginTop: 2 }}>“{it.reason}”</p> : null}
                  <CoverageStatusBadge className="mt-1.5" status={it.coverage_status} reason={it.coverage_reason} delegateName={it.coverage_delegate_name} withReason />
                  {/* No cover / cover needs attention → the approver can arrange it. */}
                  <AssignCoverAction
                    requestId={it.id}
                    employeeName={it.name}
                    employeeUserId={it.employee_user_id}
                    dates={`${it.start_date}${it.end_date && it.end_date !== it.start_date ? ` – ${it.end_date}` : ''}`}
                    coverageStatus={it.coverage_status}
                    coverageReason={it.coverage_reason}
                    coverageDelegateName={it.coverage_delegate_name}
                    onAssigned={(c) => setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, ...c } : x)))}
                  />
                  {it.coverage_attestation?.statement ? (
                    <p style={{ fontSize: 11, color: C.charcoalMid, marginTop: 4, display: 'flex', gap: 5, alignItems: 'baseline' }}>
                      <ShieldCheck size={12} style={{ flexShrink: 0, transform: 'translateY(1px)' }} />
                      <span>
                        “{it.coverage_attestation.statement}” —{' '}
                        <strong style={{ fontWeight: 600 }}>{it.coverage_attestation.signed_name}</strong>
                        {it.coverage_attestation.signed_at ? `, ${formatDate(it.coverage_attestation.signed_at)}` : ''}
                      </span>
                    </p>
                  ) : null}
                </div>
                {rejecting !== it.id ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" disabled={busy === it.id} onClick={() => approve(it.id)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 8, border: 'none', background: C.green, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}><Check size={14} /> Approve</button>
                    <button type="button" disabled={busy === it.id} onClick={() => { setRejecting(it.id); setReason(''); }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.red}`, background: 'transparent', color: C.red, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}><X size={14} /> Reject</button>
                  </div>
                ) : null}
              </div>
              {rejecting === it.id ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                  <input autoFocus placeholder="Reason for rejection" value={reason} onChange={(e) => setReason(e.target.value)}
                    style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.charcoal, fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
                  <button type="button" disabled={busy === it.id} onClick={() => confirmReject(it.id)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: C.red, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Confirm</button>
                  <button type="button" onClick={() => setRejecting(null)} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: C.charcoalMid, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApprovalsPanel;
